import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { api } from '../services/firebaseFunctions';
import PageHeader from '../interaction/PageHeader';

function CompanyManager() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    cid: '',
    name: '',
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const companiesData = await api.listCompanies();
      setCompanies(companiesData);
    } catch (err) {
      setError(err.message);
      console.error('Error loading companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({ cid: '', name: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ cid: '', name: '' });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      const companyData = { name: formData.name };
      if (formData.cid) {
        companyData.cid = parseInt(formData.cid);
      }
      await api.createCompany(companyData);
      handleCloseDialog();
      loadCompanies();
    } catch (err) {
      setError(err.message);
      console.error('Error creating company:', err);
    }
  };

  const handleToggleStatus = async (company) => {
    try {
      setError(null);
      const newStatus = company.status === 'active' ? 'inactive' : 'active';
      await api.updateCompany(company.id, { status: newStatus });
      loadCompanies();
    } catch (err) {
      setError(err.message);
      console.error('Error updating company status:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Companies"
        subtitle="The companies users belong to. A user's company scopes what they can see across SofiaPulse."
        className="ph--page"
        slot={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            Create Company
          </Button>
        }
      />
      <Card>
        <CardContent>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by ID, name, or CID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Cid</TableCell>
                  <TableCell align="right">Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...companies]
                  .filter(company => {
                    const q = searchQuery.toLowerCase();
                    return !q || company.id?.toLowerCase().includes(q) || company.name?.toLowerCase().includes(q) || String(company.cid || '').includes(q);
                  })
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {company.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{company.name}</TableCell>
                    <TableCell>{company.cid || '—'}</TableCell>
                    <TableCell align="right">
                      <Switch
                        checked={(company.status || 'active') === 'active'}
                        onChange={() => handleToggleStatus(company)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {companies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No companies found. Create your first company to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Cid"
              type="number"
              value={formData.cid}
              onChange={(e) => setFormData({ ...formData, cid: e.target.value })}
              margin="normal"
              helperText="Numeric ID for experience-api integration (optional)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompanyManager;
